import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import { APPLICATION_FORM_TYPE_RENTER_PREQUAL } from 'common/householdApplication/applicationFormFields';
import {
  FEED_CATEGORY_QUALIFIED_TO_RENT,
  FEED_CATEGORY_EXCLUSIVE_APARTMENTS,
} from 'common/feedCategoryConstants';
import { numberWithCommas } from 'common/formatters';
import {
  withFeedCategory,
  actionFeedCategoryLoadlistings,
} from 'client/helpers/feedCategoryHelpers';
import { getListingManagedPhotoUrls, isFallbackImage } from 'common/listingHelpers';
import { getActiveApartmentSpecialDeals } from 'common/apartmentHelpers';
import redirectToApplicationFormAction from 'client/actions/Household/redirectToApplicationForm';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import branch from 'client/core/branch';

import SrButtonDeprecated from 'client/components/SrButtonDeprecated';
import ListingCard from 'client/components/ListingCard';
import SrPartnerListingCard, {
  SR_PARTNER_LISTING_CARD_TAG_THEME_GREEN,
} from 'client/components/SrPartnerListingCard';

@withFeedCategory((props) => ({
  feedCategoryId: props.feedCategoryId,
  skipInitialize: true,
}))
@withInfiniteScroll({
  offset: 60,
  savePositionWithId: (props) => {
    return props.feedCategoryId;
  },
})
@branch({
  totalListingsCount: ['listings', 'totalListingsCount'],
})
class SrFeedCategoryListView extends Component {
  static propTypes = {
    feedCategoryId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    listings: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    inProgress: PropTypes.bool.isRequired,
    isFullyLoaded: PropTypes.bool.isRequired,
    currentUser: PropTypes.object.isRequired,
    renterProfile: PropTypes.object,
    listingPhotoResolution: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    market: PropTypes.object.isRequired,
    totalListingsCount: PropTypes.number.isRequired,
  };

  constructor(props) {
    super(props);

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._onLoadMore);
  }

  _onLoadMore = () => {
    const { feedCategoryId, isFullyLoaded, inProgress, dispatch } = this.props;
    if (!isFullyLoaded && !inProgress) {
      dispatch(actionFeedCategoryLoadlistings, { feedCategoryId });
    }
  };

  _onClickEditRenterPrequal = () => {
    const { dispatch } = this.props;
    dispatch(redirectToApplicationFormAction, {
      formType: APPLICATION_FORM_TYPE_RENTER_PREQUAL,
      shouldRestart: true,
    });
  };

  render() {
    const {
      title,
      total,
      inProgress,
      currentUser,
      renterProfile,
      listingPhotoResolution,
      history,
      onInfiniteScrollWrapperRef,
      feedCategoryId,
      market,
      totalListingsCount,
    } = this.props;

    const listings =
      feedCategoryId === FEED_CATEGORY_EXCLUSIVE_APARTMENTS
        ? this.props.listings
        : this.props.listings.filter((listing) => {
            const managedPhotoUrls = getListingManagedPhotoUrls({ listing });
            return managedPhotoUrls && managedPhotoUrls[0] && !isFallbackImage(managedPhotoUrls[0]);
          });

    const listingResult = `${total} out of ${numberWithCommas(totalListingsCount)} listings in ${
      market.displayName
    }, ${market.stateCode}`; // eslint-disable-line max-len

    return (
      <div className="SrPageContainer" ref={onInfiniteScrollWrapperRef}>
        <div className="SrPageContent">
          <div className="SrListviewHeader">
            <div className="SrListviewTitle">
              <span>{title}</span>
            </div>
            <div className="SrListviewSubtitle">
              <span>{listingResult}</span>
            </div>
            {feedCategoryId === FEED_CATEGORY_QUALIFIED_TO_RENT && (
              <span className="editLink" onClick={this._onClickEditRenterPrequal}>
                Edit my qualifications
              </span>
            )}
          </div>
          <div className="SrListviewGrid">
            {feedCategoryId !== FEED_CATEGORY_EXCLUSIVE_APARTMENTS &&
              listings.map((listing, index) => {
                return (
                  <ListingCard
                    className={`categoryListing${index + 1}`}
                    requestedFrom="feedCategory"
                    enableBookmarkButton
                    key={listing.id}
                    listing={listing}
                    currentUser={currentUser}
                    showRequestShowingButton={false}
                    renterProfile={renterProfile}
                    photoResolution={listingPhotoResolution}
                  />
                );
              })}
            {feedCategoryId === FEED_CATEGORY_EXCLUSIVE_APARTMENTS &&
              listings.map(({ apartmentSpecialDeals, ...apartment }) => {
                const {
                  priceRange,
                  activeListings,
                  floorPlansCount,
                  hasSelfShowingUnits,
                  has3DTourUnits,
                } = apartment.fieldsForDisplay;
                const activeApartmentSpecialDeals = getActiveApartmentSpecialDeals({
                  apartmentSpecialDeals,
                });
                const tags = activeApartmentSpecialDeals.map((apartmentSpecialDeal) => ({
                  label:
                    (apartmentSpecialDeal.floorPlanId ? '*' : '') + apartmentSpecialDeal.headline,
                  theme: SR_PARTNER_LISTING_CARD_TAG_THEME_GREEN,
                }));

                return (
                  <SrPartnerListingCard
                    className="categoryApartmentListView"
                    key={apartment.id}
                    to={`/apartment/${apartment.id}`}
                    photos={apartment.photoUrls}
                    useCarousel
                    title={priceRange}
                    caption={apartment.name}
                    tags={tags}
                    stats={[
                      floorPlansCount
                        ? `${floorPlansCount} ${pluralize('floorplan', floorPlansCount)}`
                        : `${activeListings} unit ${pluralize('type', activeListings)}`,
                    ]}
                    hasSelfTour={hasSelfShowingUnits}
                    has3DTour={has3DTourUnits}
                  />
                );
              })}
            {inProgress && <div className="loading-gif clearfix" />}
          </div>
          <SrButtonDeprecated
            text="Change your filters"
            onClick={() => history.push('/search')}
            leftIconClassName="gear"
            className="yellow"
          />
        </div>
      </div>
    );
  }
}

SrFeedCategoryListView.displayName = 'SrFeedCategoryListView';

export default SrFeedCategoryListView;
